import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ShoppingCart, X, CreditCard, Loader2, AlertCircle, Banknote } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { useToast } from '../common/Toast';
import { clearStoredSelections } from '../../hooks/useSuggestedCourses';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import { TermsModal, CoursePoliciesModal, ZelleModal, CashModal, AnimatedPrice } from './paymentModals';

// Bottom Sheet Modal for full payment details
function CheckoutBottomSheet({
  isOpen,
  onClose,
  selectedCoursesDetails,
  totalFees,
  studentId,
  agreedToTerms,
  setAgreedToTerms,
  hasAgreedToTermsOnce,
  agreedToCoursePolicy,
  setAgreedToCoursePolicy,
  hasAgreedToCoursePolicyOnce,
  onOpenTerms,
  onOpenCoursePolicies,
  onOpenZelle,
  onOpenCash,
  offlineLoading,
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isClosing, setIsClosing] = useState(false);

  // Derive shouldRender from isOpen or isClosing (animation in progress)
  const shouldRender = isOpen || isClosing;

  useBodyScrollLock(shouldRender && !isClosing);

  const handleClose = useCallback(() => {
    setIsClosing(true);
  }, []);

  // Handle animation end to complete close
  const handleTransitionEnd = useCallback(() => {
    if (isClosing) {
      setIsClosing(false);
      onClose();
    }
  }, [isClosing, onClose]);

  // Both agreements required for payment
  const canProceed = agreedToTerms && agreedToCoursePolicy;

  const handleCheckout = async () => {
    if (selectedCoursesDetails.length === 0 || totalFees.total === 0 || !canProceed) return;

    setLoading(true);
    setError(null);

    try {
      const studentCourseIds = selectedCoursesDetails.map(c => c.student_course_id);
      const { data } = await api.post(
        `/parent/children/${studentId}/payments/create-checkout-session`,
        { studentCourseIds }
      );
      window.location.href = data.url;
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.error || 'Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  if (!shouldRender) return null;

  return createPortal(
    <div className="fixed inset-0 z-[250] flex flex-col justify-end">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
          isClosing ? 'opacity-0' : 'opacity-100'
        }`}
        onClick={handleClose}
        onTransitionEnd={handleTransitionEnd}
      />

      {/* Bottom Sheet */}
      <div className={`relative bg-white dark:bg-gray-800 rounded-t-2xl shadow-2xl max-h-[85vh] flex flex-col transition-transform duration-300 ease-out ${
        isClosing ? 'translate-y-full' : 'animate-slide-up'
      }`}>
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 pb-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#589DD5]" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('checkout.paymentSummary')}</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4 overscroll-contain">
          {/* Course List */}
          <div className="space-y-2 mb-4">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('checkout.coursesSelected', { count: selectedCoursesDetails.length })}
            </p>
            {selectedCoursesDetails.map((course) => {
              const courseFee = (parseFloat(course.tuition_fee) || 0) +
                               (parseFloat(course.textbook_fee) || 0) +
                               (parseFloat(course.exam_fee) || 0);
              return (
                <div key={course.student_course_id} className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                  <span className="text-sm text-gray-600 dark:text-gray-400 truncate flex-1 min-w-0">{course.course_title}</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white shrink-0 ml-2">${courseFee.toFixed(2)}</span>
                </div>
              );
            })}
          </div>

          {/* Fee Breakdown */}
          <div className="space-y-1 text-sm mb-4 pt-2 border-t border-gray-200 dark:border-gray-700">
            {totalFees.tuition > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">{t('checkout.tuition')}</span>
                <span className="font-medium dark:text-white">${totalFees.tuition.toFixed(2)}</span>
              </div>
            )}
            {totalFees.textbook > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">{t('checkout.textbooks')}</span>
                <span className="font-medium dark:text-white">${totalFees.textbook.toFixed(2)}</span>
              </div>
            )}
            {totalFees.exam > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">{t('checkout.examFees')}</span>
                <span className="font-medium dark:text-white">${totalFees.exam.toFixed(2)}</span>
              </div>
            )}
          </div>

          {/* Payment Totals */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mb-4 space-y-2">
            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              {t('checkout.paymentTotals')}
            </p>
            {/* Cash/Zelle */}
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('checkout.cashZelle')}</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                ${totalFees.total.toFixed(2)}
              </span>
            </div>
            {/* Card (US) - 4% fee */}
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {t('checkout.cardUS')} <span className="text-xs text-gray-400 dark:text-gray-500">+4%</span>
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                ${(totalFees.total * 1.04).toFixed(2)}
              </span>
            </div>
            {/* Card (KR) - 4.5% fee */}
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {t('checkout.cardKR')} <span className="text-xs text-gray-400 dark:text-gray-500">+4.5%</span>
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                ${(totalFees.total * 1.045).toFixed(2)}
              </span>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Terms Agreement */}
          <div className="flex items-center gap-2.5 mb-2">
            <div
              className={`w-5 h-5 rounded border-2 transition-all flex items-center justify-center shrink-0 cursor-pointer ${
                agreedToTerms
                  ? 'bg-[#589DD5] border-[#589DD5]'
                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
              }`}
              onClick={() => {
                if (hasAgreedToTermsOnce) {
                  setAgreedToTerms(!agreedToTerms);
                } else {
                  onOpenTerms();
                }
              }}
            >
              <svg
                className={`w-3 h-3 text-white transition-opacity ${agreedToTerms ? 'opacity-100' : 'opacity-0'}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" className={agreedToTerms ? 'animate-checkmark' : ''} />
              </svg>
            </div>
            <span className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
              {t('checkout.agreeToTerms')}{' '}
              <span
                className="text-[#589DD5] hover:text-[#4a8bc4] underline cursor-pointer"
                onClick={onOpenTerms}
              >
                {t('checkout.termsPolicy')}
              </span>
            </span>
          </div>

          {/* Course Policy Agreement */}
          <div className="flex items-center gap-2.5 mb-4">
            <div
              className={`w-5 h-5 rounded border-2 transition-all flex items-center justify-center shrink-0 cursor-pointer ${
                agreedToCoursePolicy
                  ? 'bg-[#589DD5] border-[#589DD5]'
                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
              }`}
              onClick={() => {
                if (hasAgreedToCoursePolicyOnce) {
                  setAgreedToCoursePolicy(!agreedToCoursePolicy);
                } else {
                  onOpenCoursePolicies();
                }
              }}
            >
              <svg
                className={`w-3 h-3 text-white transition-opacity ${agreedToCoursePolicy ? 'opacity-100' : 'opacity-0'}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" className={agreedToCoursePolicy ? 'animate-checkmark' : ''} />
              </svg>
            </div>
            <span className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
              {t('checkout.agreeToCoursePolicy')}{' '}
              <span
                className="text-[#589DD5] hover:text-[#4a8bc4] underline cursor-pointer"
                onClick={onOpenCoursePolicies}
              >
                {t('checkout.courseRefundPolicy')}
              </span>
            </span>
          </div>
        </div>

        {/* Footer with Payment Buttons */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 space-y-2">
          {/* Pay with Cash */}
          <button
            onClick={onOpenCash}
            disabled={loading || offlineLoading || totalFees.total === 0 || !canProceed}
            className="w-full py-3 px-4 bg-[#589DD5] hover:bg-[#4a8bc4] text-white font-medium rounded-lg transition-colors disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <Banknote className="w-4 h-4" />
            {t('checkout.payWithCash')}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 my-1">
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
            <span className="text-xs text-gray-400 dark:text-gray-500">{t('checkout.or')}</span>
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
          </div>

          {/* Pay with Zelle */}
          <button
            onClick={onOpenZelle}
            disabled={loading || offlineLoading || totalFees.total === 0 || !canProceed}
            className="w-full py-3 px-4 bg-[#6d1ed4] hover:bg-[#5a18b0] text-white font-medium rounded-lg transition-colors disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {t('checkout.payWith')}
            <img src="/assets/zelle-logo.svg" alt="Zelle" className="h-4 brightness-0 invert" />
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 my-1">
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
            <span className="text-xs text-gray-400 dark:text-gray-500">{t('checkout.or')}</span>
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
          </div>

          {/* Pay with Card */}
          <button
            onClick={handleCheckout}
            disabled={loading || offlineLoading || totalFees.total === 0 || !canProceed}
            className="w-full py-3 px-4 bg-[#589DD5] hover:bg-[#4a8bc4] text-white font-medium rounded-lg transition-colors disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('checkout.processing')}
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4" />
                {t('checkout.payWithCard')}
              </>
            )}
          </button>

          {/* Stripe Badge */}
          <a
            href="https://stripe.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1.5 mt-2 opacity-60 hover:opacity-80 transition-opacity"
            aria-label="Powered by Stripe - Learn more"
          >
            <span className="text-xs text-gray-400 dark:text-gray-500">{t('checkout.poweredBy')}</span>
            <picture className="dark:hidden">
              <source srcSet="/assets/stripe-wordmark.svg" type="image/svg+xml" />
              <source srcSet="/assets/stripe-wordmark-small.png 1x, /assets/stripe-wordmark-large.png 2x" type="image/png" />
              <img src="/assets/stripe-wordmark-small.png" alt="Stripe" className="h-5" />
            </picture>
            <picture className="hidden dark:block">
              <source srcSet="/assets/stripe-wordmark-white.svg" type="image/svg+xml" />
              <source srcSet="/assets/stripe-wordmark-white-small.png 1x, /assets/stripe-wordmark-white-large.png 2x" type="image/png" />
              <img src="/assets/stripe-wordmark-white-small.png" alt="Stripe" className="h-5" />
            </picture>
          </a>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Animated count badge - animates when count changes
function AnimatedCount({ count }) {
  const [isAnimating, setIsAnimating] = useState(false);

  // Trigger animation when count changes - setState is intentional for animation
  useEffect(() => {
    setIsAnimating(true); // eslint-disable-line react-hooks/set-state-in-effect -- Animation trigger on prop change
    const timer = setTimeout(() => setIsAnimating(false), 150);
    return () => clearTimeout(timer);
  }, [count]);

  return (
    <span
      className={`absolute -top-4 -right-4 min-w-5 h-5 px-1 bg-sky-100 text-[#589DD5] text-[10px] font-bold rounded-full flex items-center justify-center shadow-sm ring-1 ring-[#589DD5]/20 transition-transform duration-150 ${
        isAnimating ? 'scale-125' : 'scale-100'
      }`}
    >
      {count}
    </span>
  );
}

// Floating Checkout FAB
export default function MobileCheckoutBar({ selectedCoursesDetails, totalFees, studentId, onPaymentSuccess }) {
  const toast = useToast();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [hasAgreedToTermsOnce, setHasAgreedToTermsOnce] = useState(false);
  const [agreedToCoursePolicy, setAgreedToCoursePolicy] = useState(false);
  const [hasAgreedToCoursePolicyOnce, setHasAgreedToCoursePolicyOnce] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showCoursePoliciesModal, setShowCoursePoliciesModal] = useState(false);
  const [showZelleModal, setShowZelleModal] = useState(false);
  const [showCashModal, setShowCashModal] = useState(false);
  const [offlineLoading, setOfflineLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHiding, setIsHiding] = useState(false);
  const prevCountRef = useRef(0);
  const expandTimeoutRef = useRef(null);
  const collapseTimeoutRef = useRef(null);

  // Track courses that already have Zelle payment submitted
  const [zelleSubmittedIds, setZelleSubmittedIds] = useState(() => new Set(
    selectedCoursesDetails
      .filter(c => c.zelle_payment_submitted_at)
      .map(c => c.student_course_id)
  ));

  // Update zelle submitted IDs when course details change
  useEffect(() => {
    const submittedIds = new Set(
      selectedCoursesDetails
        .filter(c => c.zelle_payment_submitted_at)
        .map(c => c.student_course_id)
    );
    setZelleSubmittedIds(submittedIds);
  }, [selectedCoursesDetails]);

  const allCoursesAlreadySubmitted = selectedCoursesDetails.length > 0 &&
    selectedCoursesDetails.every(c => zelleSubmittedIds.has(c.student_course_id));

  const hasItems = selectedCoursesDetails.length > 0;
  const shouldRender = hasItems || isHiding;

  // Both agreements required for payment
  const canProceed = agreedToTerms && agreedToCoursePolicy;

  const handleOfflinePayment = async (paymentMethod) => {
    if (!hasItems || totalFees.total === 0 || !canProceed) return;

    setOfflineLoading(true);

    try {
      const studentCourseIds = selectedCoursesDetails.map(c => c.student_course_id);
      const { data } = await api.post(
        `/parent/children/${studentId}/payments/offline-payment-request`,
        { studentCourseIds, paymentMethod }
      );

      if (paymentMethod === 'zelle') {
        const allSubmitted = [...(data.alreadySubmitted || []), ...(data.newlySubmitted || [])];
        if (allSubmitted.length > 0) {
          setZelleSubmittedIds(prev => {
            const newSet = new Set(prev);
            allSubmitted.forEach(id => newSet.add(id));
            return newSet;
          });
        }
      }

      setShowZelleModal(false);
      setShowCashModal(false);
      setIsSheetOpen(false);

      clearStoredSelections(studentId);

      toast.success('Payment request submitted! Our office will verify your payment and update your enrollment status.', {
        duration: 7000,
      });

      if (onPaymentSuccess) {
        onPaymentSuccess();
      }
    } catch (err) {
      console.error('Offline payment error:', err);
      toast.error(err.response?.data?.error || 'Failed to submit payment request. Please try again.');
    } finally {
      setOfflineLoading(false);
    }
  };

  // Handle expand animation when count changes
  useEffect(() => {
    const currentCount = selectedCoursesDetails.length;
    const prevCount = prevCountRef.current;

    // Clear any existing timeouts
    if (expandTimeoutRef.current) clearTimeout(expandTimeoutRef.current);
    if (collapseTimeoutRef.current) clearTimeout(collapseTimeoutRef.current);

    if (hasItems && currentCount !== prevCount) {
      const expandDelay = prevCount === 0 ? 250 : 50;

      expandTimeoutRef.current = setTimeout(() => {
        setIsExpanded(true);
      }, expandDelay);

      collapseTimeoutRef.current = setTimeout(() => {
        setIsExpanded(false);
      }, 2000 + expandDelay);

      prevCountRef.current = currentCount;
    } else if (!hasItems) {
      setIsExpanded(false);
      prevCountRef.current = 0;
    }

    return () => {
      if (expandTimeoutRef.current) clearTimeout(expandTimeoutRef.current);
      if (collapseTimeoutRef.current) clearTimeout(collapseTimeoutRef.current);
    };
  }, [hasItems, selectedCoursesDetails.length]);

  // Handle hide transition end
  const handleTransitionEnd = useCallback(() => {
    if (isHiding && !hasItems) {
      setIsHiding(false);
    }
  }, [isHiding, hasItems]);

  // Start hide animation when items are removed
  useEffect(() => {
    if (!hasItems && shouldRender && !isHiding) {
      setIsHiding(true);
    }
  }, [hasItems, shouldRender, isHiding]);

  if (!shouldRender) {
    return null;
  }

  const isVisible = hasItems && !isHiding;

  return (
    <>
      {/* Floating Action Button */}
      {createPortal(
        <button
          onClick={() => setIsSheetOpen(true)}
          onTransitionEnd={handleTransitionEnd}
          className={`fixed top-36 right-4 z-[200] lg:hidden flex items-center justify-center bg-[#589DD5] hover:bg-[#4a8bc4] text-white font-medium rounded-full shadow-lg hover:shadow-xl transition-all duration-300 ease-out active:scale-95 ${
            isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
          }`}
          style={{
            height: '44px',
            width: isExpanded ? '136px' : '44px',
            paddingLeft: isExpanded ? '16px' : '0',
            paddingRight: isExpanded ? '12px' : '0',
            gap: isExpanded ? '8px' : '0',
          }}
        >
          {/* Total - slides in/out */}
          <div
            className="overflow-hidden transition-all duration-300 ease-out"
            style={{
              width: isExpanded ? '80px' : '0px',
              opacity: isExpanded ? 1 : 0,
            }}
          >
            <span className="text-sm font-semibold whitespace-nowrap">
              <AnimatedPrice value={totalFees.total} height={20} />
            </span>
          </div>
          {/* Cart icon with badge */}
          <div className="relative shrink-0">
            <ShoppingCart className="w-5 h-5" />
            <AnimatedCount count={selectedCoursesDetails.length} />
          </div>
        </button>,
        document.body
      )}

      {/* Bottom Sheet */}
      <CheckoutBottomSheet
        isOpen={isSheetOpen}
        onClose={() => setIsSheetOpen(false)}
        selectedCoursesDetails={selectedCoursesDetails}
        totalFees={totalFees}
        studentId={studentId}
        agreedToTerms={agreedToTerms}
        setAgreedToTerms={setAgreedToTerms}
        hasAgreedToTermsOnce={hasAgreedToTermsOnce}
        agreedToCoursePolicy={agreedToCoursePolicy}
        setAgreedToCoursePolicy={setAgreedToCoursePolicy}
        hasAgreedToCoursePolicyOnce={hasAgreedToCoursePolicyOnce}
        onOpenTerms={() => setShowTermsModal(true)}
        onOpenCoursePolicies={() => setShowCoursePoliciesModal(true)}
        onOpenZelle={() => setShowZelleModal(true)}
        onOpenCash={() => setShowCashModal(true)}
        offlineLoading={offlineLoading}
      />

      {/* Terms Modal */}
      <TermsModal
        isOpen={showTermsModal}
        zIndex={300}
        onClose={() => {
          setAgreedToTerms(true);
          setHasAgreedToTermsOnce(true);
          setShowTermsModal(false);
        }}
        onDisagree={() => {
          setAgreedToTerms(false);
          setShowTermsModal(false);
        }}
      />

      {/* Course Policies Modal */}
      <CoursePoliciesModal
        isOpen={showCoursePoliciesModal}
        zIndex={300}
        courses={selectedCoursesDetails}
        onClose={() => {
          setAgreedToCoursePolicy(true);
          setHasAgreedToCoursePolicyOnce(true);
          setShowCoursePoliciesModal(false);
        }}
        onDisagree={() => {
          setAgreedToCoursePolicy(false);
          setShowCoursePoliciesModal(false);
        }}
      />

      {/* Zelle Modal */}
      <ZelleModal
        isOpen={showZelleModal}
        zIndex={300}
        onClose={() => setShowZelleModal(false)}
        onConfirm={() => handleOfflinePayment('zelle')}
        totalAmount={totalFees.total}
        isLoading={offlineLoading}
        studentId={studentId}
        studentCourseIds={selectedCoursesDetails.map(c => c.student_course_id)}
        allCoursesAlreadySubmitted={allCoursesAlreadySubmitted}
      />

      {/* Cash Modal */}
      <CashModal
        isOpen={showCashModal}
        zIndex={300}
        onClose={() => setShowCashModal(false)}
        onConfirm={() => handleOfflinePayment('cash')}
        totalAmount={totalFees.total}
        isLoading={offlineLoading}
      />
    </>
  );
}
